import { lightBlueButton } from "constants/colors";
import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import { FORMAT_READABLE_WEEK_MONTH_TIME } from "utils/timeUtils";
import {
  highlightElements,
  unhighlightAllElements,
  useDebuggerPopupState,
} from "../debuggerUtils";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import { useFormatZoned } from "utils/customHooks/genericHooks";
import { AUTH_PROFILE_NAME } from "constants/constants";
import { useCopyToClipboard } from "react-use";
import { SERVER_NAMES } from "../SERVER_NAMES";
import { useAuth0 } from "@auth0/auth0-react";
import { KeyboardArrowDown, OpenInNew } from "@mui/icons-material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

function getTokenLsKey(aud) {
  return `@@auth0spajs@@::${aud}::@@user@@`;
}

export const NetworkItemRow = memo(
  ({
    networkName,
    isNetworkOnline,
    data,
    timesUpdated,
    itemCounts,
    serverName,
    graphiqlUrlOrUrls,
    isDataRequiredOnThisPage,
    isSelected,
  }: {
    networkName: string;
    isNetworkOnline: boolean;
    data: any;
    timesUpdated: number;
    itemCounts: any;
    serverName: keyof typeof SERVER_NAMES;
    graphiqlUrlOrUrls: any;
    isDataRequiredOnThisPage: boolean;
    isSelected: boolean;
  }) => {
    const networkItemRowClass = useMemo(
      () =>
        networkName
          .toLowerCase()
          .replaceAll(/ /g, "")
          .replaceAll(/\//g, "-")
          .replaceAll("+", ""),
      [networkName]
    );
    return (
      <div
        className={`networkItemRow ${networkItemRowClass} ${isSelected ? "selected" : ""}`}
        onMouseOver={() => {
          highlightElements(serverName);
        }}
        onMouseOut={unhighlightAllElements}
      >
        <NetworkItemRowMouseHandler
          {...{ networkName, data, networkItemRowClass }}
        />
        <NetworkItemTitle
          isNetworkOnline={isNetworkOnline}
          isDataRequiredOnThisPage={isDataRequiredOnThisPage}
        />
        <div className={`networkNameContainer ${networkName}`}>
          <div className="networkName">{networkName}</div>

          <div className="itemCountsContainer">
            {itemCounts.map(({ itemName, itemCount }) => (
              <div key={itemName} className={`itemCount lightText ${itemName}`}>
                <span className="numTimes darkText">{itemCount}</span>{" "}
                {itemName}
              </div>
            ))}
            {timesUpdated ? (
              <div className="timesUpdated lightText">
                <span className="numTimes darkText">{timesUpdated}</span> update
                {timesUpdated === 1 ? "" : "s"}
              </div>
            ) : networkName === AUTH_PROFILE_NAME ? (
              <AuthProfileInfo
                {...{
                  data,
                }}
              />
            ) : null}
          </div>
        </div>
        {graphiqlUrlOrUrls &&
          (Array.isArray(graphiqlUrlOrUrls) ? (
            <GraphiqlLinksMenu
              {...{
                graphiqlUrls: graphiqlUrlOrUrls,
                isNetworkOnline,
              }}
            />
          ) : (
            <a
              href={graphiqlUrlOrUrls}
              className={`graphiqlLink${isNetworkOnline ? " active" : ""}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              graphiql <OpenInNew style={{ marginBottom: -2 }} />
            </a>
          ))}
        <div className="btnInspectWrapper">
          <IconButton className="btnInspect" size="large">
            <Search />
          </IconButton>
        </div>
        {networkName === AUTH_PROFILE_NAME ? <AuthIdTokenControls /> : null}
      </div>
    );
  }
);
NetworkItemRow.displayName = "NetworkItemRow";

function NetworkItemRowMouseHandler({
  networkName,
  data,
  networkItemRowClass,
}) {
  const [, setDebuggerPopupState] = useDebuggerPopupState();

  const handleOpenPopup = useCallback(() => {
    setDebuggerPopupState(prev => ({
      ...prev,
      isPreview: false,
      popupData: {
        networkName,
        data,
      },
    }));
    console.log(`🔍 ${networkName}`, data);
  }, [setDebuggerPopupState, networkName, data]);
  const handleMouseEnter = useCallback(() => {
    setDebuggerPopupState(prev => {
      if (prev.popupData && prev.isPopupOpen && !prev.isPreview) {
        return prev;
      } else {
        return {
          ...prev,
          popupData: { networkName, data },
          isPreview: true,
          isPopupOpen: true,
        };
      }
    });
  }, [setDebuggerPopupState, networkName, data]);
  useEffect(() => {
    const networkItemRow = document.querySelector(`.${networkItemRowClass}`);
    if (!networkItemRow) {
      return () => {};
    }
    networkItemRow.addEventListener("click", handleOpenPopup);
    networkItemRow.addEventListener("mouseenter", handleMouseEnter);
    return () => {
      networkItemRow.removeEventListener("click", handleOpenPopup);
      networkItemRow.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [networkItemRowClass, handleOpenPopup, handleMouseEnter]);
  return null;
}

function AuthProfileInfo({ data }) {
  const formatZoned = useFormatZoned();
  const { idToken, isTokenValid, idTokenExpFormatted } = useMemo(
    () => getTokenInfo(),
    []
  );
  const hasIdToken = Boolean(idToken);
  const { loginDateMs, loginDateFormatted } = useMemo(() => {
    const loginDateMs = window.localStorage.getItem("loginDateMs");
    return {
      loginDateMs,
      loginDateFormatted: loginDateMs
        ? formatZoned(Number(loginDateMs), FORMAT_READABLE_WEEK_MONTH_TIME)
        : null,
    };
  }, [formatZoned]);

  return (
    <div className="timesUpdated lightText">
      {data.user ? (
        <span className="numTimes darkText">{data.user.name}</span>
      ) : (
        <span className="warning">no user</span>
      )}
      {" / "}
      {hasIdToken ? (
        <span className="localStorageInfo">
          token expiry: {idTokenExpFormatted}
        </span>
      ) : (
        <div className="warning">no auth token</div>
      )}
      {!isTokenValid && (
        <div className="warning">auth token is invalid/expired</div>
      )}
      {loginDateMs && (
        <div className="localStorageInfo">logged in: {loginDateFormatted}</div>
      )}
    </div>
  );
}

function AuthIdTokenControls() {
  const { idToken } = useMemo(() => getTokenInfo(), []);

  const [copied, copyToClipboard] = useCopyToClipboard();
  const { getIdTokenClaims } = useAuth0();

  return (
    <div className="controls-section">
      <h1 className="title">id_token controls:</h1>
      <div className="control-buttons-section">
        <Button
          variant="outlined"
          onClick={() => {
            (async () => {
              const claims = await getIdTokenClaims();
              if (claims?.aud) {
                const lsKey = getTokenLsKey(claims.aud);
                window.localStorage.removeItem(lsKey);
              }
            })();
            window.localStorage.removeItem("id_token");
          }}
        >
          delete
        </Button>
        <Button
          variant="outlined"
          onClick={e => {
            e.stopPropagation();
            if (idToken) {
              copyToClipboard(`Bearer ${idToken}`);
            }
          }}
        >
          {copied.value ? "copied!" : "copy"}
        </Button>
      </div>
    </div>
  );
}

function GraphiqlLinksMenu({ graphiqlUrls, isNetworkOnline }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <a
        className={`graphiqlLink${isNetworkOnline ? " active" : ""}`}
        onClick={handleClick}
      >
        graphiqls <KeyboardArrowDown style={{ marginBottom: -2 }} />
      </a>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {graphiqlUrls.map(({ name, url }, idx) => {
          return (
            <MenuItem
              key={url}
              onClick={() => {
                window.open(url, "_blank");
                handleClose();
              }}
              style={{ color: lightBlueButton, textDecoration: "underline" }}
            >
              {name}&nbsp;
              <OpenInNew style={{ transform: "translateX(-5px) scale(0.6)" }} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

function NetworkItemTitle({ isNetworkOnline, isDataRequiredOnThisPage }) {
  return (
    <Tooltip
      title={
        !isDataRequiredOnThisPage && isNetworkOnline
          ? "⚠ extra data (not required on this page)"
          : !isDataRequiredOnThisPage
            ? "✅ this data is not required on this page"
            : isNetworkOnline
              ? "✅ data received"
              : "❌ waiting for required data"
      }
    >
      <div
        className={`networkStatus ${
          !isDataRequiredOnThisPage && isNetworkOnline
            ? "notRequired connected"
            : !isDataRequiredOnThisPage
              ? "notRequired"
              : isNetworkOnline
                ? "connected"
                : "notConnected"
        }`}
      >
        {!isNetworkOnline && isDataRequiredOnThisPage && (
          <div className="awaitingDataIndicator">
            <CircularProgress size={16} />
          </div>
        )}
      </div>
    </Tooltip>
  );
}
